// src/pages/Dashboard.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {GET_DASHBOARD_DATA} from "../graphql/dashboard";
import VATConfigurationModal from '../components/vat-config/VATConfigurationModal';

const Dashboard = () => {
const [isVATConfigModalOpen, setIsVATConfigModalOpen] = useState(false);
  const { loading, error, data } = useQuery(GET_DASHBOARD_DATA);

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
      <strong className="font-bold">Error!</strong>
      <span className="block sm:inline"> {error.message}</span>
    </div>
  );

  const { getDashboardData } = data;

  return (
    <div>
      <h1 className="text-3xl font-semibold mb-6">Dashboard</h1>
      
      {/* Property Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {/* Total Properties */}
        <div className="bg-blue-500 text-white rounded-lg p-6 flex flex-col items-center">
          <div className="mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium mb-2">Total Properties</h3>
          <p className="text-4xl font-bold">{getDashboardData.totalProperties}</p>
        </div>
        
        {/* Occupied Properties */}
        <div className="bg-green-500 text-white rounded-lg p-6 flex flex-col items-center">
          <div className="mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <h3 className="text-lg font-medium mb-2">Occupied Properties</h3>
          <p className="text-4xl font-bold">{getDashboardData.occupiedProperties}</p>
        </div>
        
        {/* Vacant Properties */}
        <div className="bg-yellow-500 text-white rounded-lg p-6 flex flex-col items-center">
          <div className="mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <h3 className="text-lg font-medium mb-2">Vacant Properties</h3>
          <p className="text-4xl font-bold">{getDashboardData.vacantProperties}</p>
        </div>
        
        {/* In Progress */}
        <div className="bg-purple-500 text-white rounded-lg p-6 flex flex-col items-center">
          <div className="mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium mb-2">In Progress</h3>
          <p className="text-4xl font-bold">{getDashboardData.inProgressProperties}</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Properties */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-xl font-semibold">Recent Properties</h2>
              <Link to="/properties" className="text-blue-600 hover:underline text-sm">View All</Link>
            </div>
            <div className="overflow-x-auto">
              {getDashboardData.recentProperties && getDashboardData.recentProperties.length > 0 ? (
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Landlord</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getDashboardData.recentProperties.map(property => (
                      <tr key={property.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            <Link to={`/properties/${property.id}`} className="text-blue-600 hover:underline">
                              {property.address.street}
                            </Link>
                          </div>
                          <div className="text-sm text-gray-500">
                            {property.address.suburb}, {property.address.town} {property.address.postalCode}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${(property.status === 'Rented By Us' || property.status === 'occupied') ? 'bg-green-100 text-green-800' : 
                            (property.status === 'Vacant Rental' || property.status === 'vacant') ? 'bg-yellow-100 text-yellow-800' : 
                            'bg-blue-100 text-blue-800'}`}>
                            {(property.status === 'Rented By Us' || property.status === 'occupied') ? 'Occupied' : 
                             (property.status === 'Vacant Rental' || property.status === 'vacant') ? 'Vacant' : 
                             (property.status === 'Rental Application In Progress' || property.status === 'in-progress') ? 'In Progress' : 
                             property.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {property.landlord ? `${property.landlord.firstName} ${property.landlord.lastName}` : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {property.tenant ? `${property.tenant.firstName} ${property.tenant.lastName}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="p-4 text-gray-500">No properties found</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Quick Actions & People */}
        <div className="lg:col-span-1 space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h2 className="text-xl font-semibold">Quick Actions</h2>
          </div>
          <div className="p-4 space-y-3">
            <Link to="/properties/add" className="block w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded text-center transition duration-150 ease-in-out">
              Add New Property
            </Link>
            <Link to="/landlords/add" className="block w-full py-2 px-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-medium rounded text-center transition duration-150 ease-in-out">
              Add New Landlord
            </Link>
            <Link to="/tenants/add" className="block w-full py-2 px-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-medium rounded text-center transition duration-150 ease-in-out">
              Add New Tenant
            </Link>
            <Link to="/agents/add" className="block w-full py-2 px-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-medium rounded text-center transition duration-150 ease-in-out">
              Add New Agent
            </Link>
<button 
              onClick={() => setIsVATConfigModalOpen(true)}
              className="block w-full py-2 px-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-medium rounded text-center transition duration-150 ease-in-out"
            >
              VAT Configuration
            </button>
            <Link to="/sales/pipeline-dashboard" className="block w-full py-2 px-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-medium rounded text-center transition duration-150 ease-in-out">
              Sales Pipeline Dashboard
            </Link>
          </div>
        </div>
          
          {/* People */}
          <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h2 className="text-xl font-semibold">People</h2>
          </div>
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-base font-medium">Landlords</h3>
                <p className="text-sm text-gray-500">Total registered</p>
              </div>
              <div className="bg-blue-600 text-white text-lg font-bold w-10 h-10 rounded-md flex items-center justify-center">
                {getDashboardData.totalLandlords}
              </div>
            </div>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-base font-medium">Tenants</h3>
                <p className="text-sm text-gray-500">Total registered</p>
              </div>
              <div className="bg-green-600 text-white text-lg font-bold w-10 h-10 rounded-md flex items-center justify-center">
                {getDashboardData.totalTenants}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <Link to="/landlords" className="block py-2 px-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 text-sm font-medium rounded text-center transition duration-150 ease-in-out">
                View Landlords
              </Link>
              <Link to="/tenants" className="block py-2 px-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 text-sm font-medium rounded text-center transition duration-150 ease-in-out">
                View Tenants
              </Link>
            </div>
          </div>
        </div>
        </div>
      </div>

{/* VAT Configuration Modal */}
      <VATConfigurationModal 
        isOpen={isVATConfigModalOpen}
        onClose={() => setIsVATConfigModalOpen(false)}
      />
    </div>
  );
};

export default Dashboard;